/**
 * Protected Route Component
 *
 * Wraps routes that require authentication.
 * - Redirects to login if not authenticated
 * - Optionally restricts access by user role (mapped from org role)
 * - Shows loading spinner while checking auth
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { UserRole, OrgRole } from '../../api/auth';

/**
 * Map org role to user role for route protection
 * OWNER -> SUPER_ADMIN, ADMIN -> ADMIN, CREW -> CREW
 */
function mapOrgRoleToUserRole(orgRole: OrgRole | undefined): UserRole | undefined {
  if (!orgRole) return undefined;
  switch (orgRole) {
    case 'OWNER':
      return 'SUPER_ADMIN';
    case 'ADMIN':
      return 'ADMIN';
    case 'CREW':
      return 'CREW';
  }
}

// =============================================================================
// Types
// =============================================================================

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

// =============================================================================
// Loading Spinner Component
// =============================================================================

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="flex flex-col items-center">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

// =============================================================================
// Protected Route Component
// =============================================================================

export default function ProtectedRoute({
  children,
  allowedRoles,
}: ProtectedRouteProps) {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const { currentOrg, isLoading: orgLoading } = useOrganization();
  const location = useLocation();

  // Map org role to user role for comparison
  const userRole = mapOrgRoleToUserRole(currentOrg?.role);

  // Platform admins get SUPER_ADMIN access even without org membership
  const isPlatformAdmin = user?.platformRole === 'PLATFORM_ADMIN';
  const effectiveRole = userRole || (isPlatformAdmin ? 'SUPER_ADMIN' : undefined);

  // Show loading spinner while checking auth or org status
  if (authLoading || orgLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for PLATFORM_ADMIN-only routes
  if (allowedRoles?.includes('PLATFORM_ADMIN')) {
    if (!isPlatformAdmin) {
      // Non-platform-admins cannot access platform routes
      if (effectiveRole === 'CREW') {
        return <Navigate to="/crew" replace />;
      } else {
        return <Navigate to="/admin" replace />;
      }
    }
    // Platform admin can access, allow through
    return <>{children}</>;
  }

  // Check role-based access using effective role (org role or platform role)
  if (allowedRoles && effectiveRole && !allowedRoles.includes(effectiveRole)) {
    // User doesn't have required role
    // Redirect crew to crew dashboard, admins to admin dashboard
    if (effectiveRole === 'CREW') {
      return <Navigate to="/crew" replace />;
    } else {
      return <Navigate to="/admin" replace />;
    }
  }

  // User is authenticated and has required role (if specified)
  return <>{children}</>;
}
